import { FIREWORK_EVENTS } from "../../utils/newRelicTracking";

export type StoryblockHandlers = {
  [key: string]: EventListener | Record<string, EventListener> | undefined;
  fireworkEventHandlers?: Record<string, EventListener>;
  html5TrackerImpressionHandler?: EventListener;
};

type ListenerRegistration = {
  target: EventTarget;
  eventName: string;
  handler: EventListener;
};

type StoryblockNode = HTMLElement & {
  __storyBlockHandlers?: StoryblockHandlers;
  __storyBlockListenerRegistrations?: ListenerRegistration[];
};

const DIRECT_HANDLER_EVENTS: ReadonlyArray<
  readonly [handlerName: string, eventNames: readonly string[]]
> = [
  ["thumbnailClick", ["fw:video:click-cta", "fw:storyblock:thumbnail-click"]],
  ["contentReady", ["fw:video:impression"]],
  ["videoGenerated", ["fw:storyblock:impression"]],
  ["videoStart", ["fw:video:start"]],
  ["videoComplete", ["fw:video:complete"]],
  ["videoMute", ["fw:video:mute"]],
  ["playerState", ["fw:player:state"]],
  ["videoFullscreen", ["fw:video:fullscreen"]],
  ["playerMinimize", ["fw:player:minimize"]],
  ["playerMaximize", ["fw:player:maximize"]],
  ["pipMode", ["fw:player:transition"]],
  ["pipAction", ["fw:player:quit", "fw:player:navigate"]],
];

const ALL_FIREWORK_EVENTS = Object.values(FIREWORK_EVENTS).flatMap((group) => Object.values(group));

function getTargets(node: HTMLElement): EventTarget[] {
  return node.shadowRoot ? [node, node.shadowRoot] : [node];
}

function getHandler(handlers: StoryblockHandlers, handlerName: string): EventListener | undefined {
  const handler = handlers[handlerName];
  return typeof handler === "function" ? handler : undefined;
}

function addListeners(
  target: EventTarget,
  eventNames: readonly string[],
  handler: EventListener | undefined,
  registrations: ListenerRegistration[]
): void {
  if (!handler) return;

  for (const eventName of eventNames) {
    target.addEventListener(eventName, handler);
    registrations.push({ target, eventName, handler });
  }
}

function registerDirectHandlers(
  target: EventTarget,
  handlers: StoryblockHandlers,
  registrations: ListenerRegistration[]
): void {
  for (const [handlerName, eventNames] of DIRECT_HANDLER_EVENTS) {
    addListeners(target, eventNames, getHandler(handlers, handlerName), registrations);
  }
}

function registerFireworkHandlers(
  target: EventTarget,
  handlers: StoryblockHandlers,
  registrations: ListenerRegistration[]
): void {
  const fireworkHandlers = handlers.fireworkEventHandlers ?? {};

  for (const eventName of ALL_FIREWORK_EVENTS) {
    addListeners(target, [eventName], fireworkHandlers[eventName], registrations);
  }
}

function registerTargetListeners(
  target: EventTarget,
  handlers: StoryblockHandlers,
  fwProductClickEvents: string[],
  registrations: ListenerRegistration[]
): void {
  addListeners(target, fwProductClickEvents, getHandler(handlers, "productClick"), registrations);
  registerDirectHandlers(target, handlers, registrations);
  registerFireworkHandlers(target, handlers, registrations);
  addListeners(
    target,
    ["fw:video:impression"],
    handlers.html5TrackerImpressionHandler,
    registrations
  );
}

export function registerStoryblockListeners(
  fireworkNode: HTMLElement,
  handlers: StoryblockHandlers,
  fwProductClickEvents: string[]
): void {
  if (!fireworkNode) return;

  const registrations: ListenerRegistration[] = [];
  for (const target of getTargets(fireworkNode)) {
    registerTargetListeners(target, handlers, fwProductClickEvents, registrations);
  }

  const storyblockNode = fireworkNode as StoryblockNode;
  storyblockNode.__storyBlockHandlers = handlers;
  storyblockNode.__storyBlockListenerRegistrations = registrations;
}

export function cleanupStoryblockListeners(node: HTMLElement | null): void {
  if (!node) return;

  const storyblockNode = node as StoryblockNode;
  const registrations = storyblockNode.__storyBlockListenerRegistrations ?? [];
  for (const { target, eventName, handler } of registrations) {
    target.removeEventListener(eventName, handler);
  }

  delete storyblockNode.__storyBlockListenerRegistrations;
  delete storyblockNode.__storyBlockHandlers;
}
